import React from 'react';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getCoatingTypeName, getHasCooling, getHasPoCapacity, getHeatingMedium, getIbcTankType, getImoTankType } from 'src/utils/stowage/tankUtils';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { useStowageStore } from 'src/context/StowageContext';
import { OverlayMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { TankDetailsTable } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { AdjacentTank } from 'src/types/ViewModel/Vessel/AdjacentTank';

interface TankDetailsProps {
    tank: Tank;
}

const renderTankDetails = (tank: Tank) => {
    const getAdjacentTankNames = (adjacentTanks: AdjacentTank[]): React.ReactNode => {
        if (!adjacentTanks || adjacentTanks.length === 0) {
            return 'None';
        }

        return adjacentTanks.map((tank) => tank.tank.odfjellTankName).join(', ');
    };

    return (
        <TankDetailsTable>
            <tbody>
                <tr key={'tank_details_coatingType'}>
                    <td>Coating type</td>
                    <td>{getCoatingTypeName(tank.coatingType)}</td>
                </tr>
                <tr key={'tank_details_coating'}>
                    <td>Coating</td>
                    <td>{tank.coating.name}</td>
                </tr>
                <tr key={'tank_details_maxVapourPressure'}>
                    <td>Max. vapour pressure</td>
                    <td>{tank.maxVapourPressure} bar</td>
                </tr>
                <tr key={'tank_details_ibcTankType'}>
                    <td>IBC tank type</td>
                    <td>{getIbcTankType(tank.ibcTankType)}</td>
                </tr>
                <tr key={'tank_details_imoTankType'}>
                    <td>IMO tank type</td>
                    <td>{getImoTankType(tank.imoTankType)}</td>
                </tr>
                <tr key={'tank_details_hasPoCapacity'}>
                    <td>PO capacity</td>
                    <td>{getHasPoCapacity(tank.hasPoCapacity)}</td>
                </tr>
                <tr key={'tank_details_heatingType'}>
                    <td>Heating medium</td>
                    <td>{getHeatingMedium(tank.heatingType)}</td>
                </tr>
                <tr key={'tank_details_maxHeating'}>
                    <td>Max. heating</td>
                    <td>{tank.maxHeating} °C</td>
                </tr>
                <tr key={'tank_details_heatingCapacity'}>
                    <td>Heating capacity</td>
                    <td>{tank.heatingCapacity} °C</td>
                </tr>
                <tr key={'tank_details_hasCooling'}>
                    <td>Cooling capacity</td>
                    <td>{getHasCooling(tank.hasCooling)}</td>
                </tr>
                <tr key={'tank_details_litreWeightSoftRule'}>
                    <td>Max. litre weight for 98% filling (soft rule)</td>
                    <td>{tank.litreWeightSoftRule} kg/L</td>
                </tr>
                <tr key={'tank_details_litreWeightHardRule'}>
                    <td>Max. litre weight for 98% filling (hard rule)</td>
                    <td>{tank.litreWeightHardRule} kg/L</td>
                </tr>
                {tank.ibcCompatibilities?.length > 0 && (
                    <tr>
                        <td>IBC compatibilities</td>
                        <td>{tank.ibcCompatibilities.map((o) => o.replace(/IBC-/g, '')).join(', ')}</td>
                    </tr>
                )}
                <tr key={'tank_details_adjacentTanks'}>
                    <td>Adjacent tanks</td>
                    <td>{getAdjacentTankNames(tank.adjacentTanks)}</td>
                </tr>
            </tbody>
        </TankDetailsTable>
    );
};

const TankDetailsContainer: React.FC<TankDetailsProps> = ({ tank }) => {
    const { setOverlayMenuState } = useStowageStore();

    const onClosePanel = () => {
        setOverlayMenuState({ visible: false } as OverlayMenuState);
    };

    return (
        <OdfjellPanel
            headerTitle={tank.odfjellTankName}
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
            onCancel={onClosePanel}
            onAction={onClosePanel}
            actionButtonText="Close"
        >
            <div className="tank-details-content">{tank && renderTankDetails(tank)}</div>
        </OdfjellPanel>
    );
};

export default TankDetailsContainer;
